import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Bar,
  Line,
} from "recharts";
import { getWeeklyLeetCodeActivityTrends } from "../../services/leetcodeAnalyticsService";
import { dashboardTooltipSurfaceSx, PRIMARY, INDIGO } from "../../utils/colors";

const WEEK_OPTIONS = [4, 8, 12, 24];

const TrendsTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const studentsEntry = payload.find((p) => p.dataKey === "activeStudents");
  const solvedEntry = payload.find((p) => p.dataKey === "problemsSolved");
  const row = payload[0]?.payload;

  return (
    <Box
      sx={{
        ...dashboardTooltipSurfaceSx,
        borderRadius: 2,
        p: 1.25,
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
        {label}
      </Typography>
      {studentsEntry && (
        <Typography variant="body2" fontWeight={600}>
          {studentsEntry.name}: {studentsEntry.value}
        </Typography>
      )}
      {solvedEntry && (
        <Typography variant="body2" fontWeight={600}>
          {solvedEntry.name}: {solvedEntry.value}
        </Typography>
      )}
      {row && (
        <Typography variant="body2" fontWeight={600}>
          Avg Problems/Student: {row.avgProblemsPerStudent}
        </Typography>
      )}
    </Box>
  );
};

const LeetCodeActivityTrendsModal = ({ open, onClose, labId, batchId }) => {
  const [weeks, setWeeks] = useState(12);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open) return;

    let isMounted = true;
    setLoading(true);
    setError("");

    getWeeklyLeetCodeActivityTrends({ weeks, labId, batchId })
      .then((data) => {
        if (!isMounted) return;
        setRows(data);
      })
      .catch((err) => {
        if (!isMounted) return;
        setRows([]);
        setError(err?.response?.data?.message || err?.message || "Failed to load weekly trends.");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [open, weeks, labId, batchId]);

  const hasData = useMemo(() => Array.isArray(rows) && rows.length > 0, [rows]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          Weekly Activity Trends
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              value={weeks}
              onChange={(e) => setWeeks(Number(e.target.value))}
              displayEmpty
            >
              {WEEK_OPTIONS.map((w) => (
                <MenuItem key={w} value={w}>
                  Last {w} weeks
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton onClick={onClose} size="small">
            <CloseRoundedIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        {loading && (
          <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={34} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && !hasData && (
          <Box sx={{ py: 10, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary" fontWeight={600}>
              No weekly activity data available.
            </Typography>
          </Box>
        )}

        {!loading && !error && hasData && (
          <Box sx={{ width: "100%", height: 420 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={rows} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                <XAxis dataKey="week" />
                <YAxis
                  yAxisId="left"
                  allowDecimals={false}
                  label={{ value: "Active Students", angle: -90, position: "insideLeft" }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  allowDecimals={false}
                  label={{ value: "Problems Solved", angle: 90, position: "insideRight" }}
                />
                <Tooltip content={<TrendsTooltip />} />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="activeStudents"
                  name="Active Students"
                  fill={PRIMARY}
                  radius={[6, 6, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="problemsSolved"
                  name="Problems Solved"
                  stroke={INDIGO}
                  strokeWidth={2.5}
                  dot={{ r: 2 }}
                  activeDot={{ r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeetCodeActivityTrendsModal;
